/**
 * The walk's physical facts - eye height, body radius, and how a position is
 * pushed back out of the furniture.
 *
 * Split from Player.ts because two very different callers need the same walls.
 * The browser Player drives this every frame; the headless layout harness
 * (scripts/subfab-check.ts, bundled by esbuild with no DOM) reasons about the
 * same clearances to prove the stairwell is walkable. Keeping the physics in a
 * file with no input handling is what lets Player.ts grow browser-only
 * concerns - and, eventually, sit inside a React-mounted canvas - without the
 * harness ever bundling any of that.
 */

import * as THREE from 'three'
import { FAB } from './layout'
import { SUBFAB } from './subfabLayout'
import { clamp } from '../core/math'

export const EYE_HEIGHT = 1.68
export const PLAYER_RADIUS = 0.42

export interface Collider {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
  /**
   * Vertical extent, world metres. Absent means "all heights" - the right
   * default for a wall. With two walkable levels a collider usually belongs
   * to one of them: a pump skid on the sub-fab slab must not block the student
   * walking the fab floor three metres above it, and vice versa.
   */
  minY?: number
  maxY?: number
}

/**
 * Push a position out of any collider it has entered, resolving along the
 * shallowest axis. Crude compared to a real character controller, but for a
 * room of axis-aligned boxes it never lets you through a wall and never
 * catches on a corner.
 *
 * `velocity`, when given, has its into-the-wall component clamped so the
 * mover slides along faces instead of grinding to a halt against them.
 */
export function resolveCollisions(
  pos: THREE.Vector3,
  foot: number,
  colliders: readonly Collider[],
  velocity?: THREE.Vector3,
): void {
  if (foot < -0.6) {
    // Below the fab floor the world is the sub-fab room, whose walls are
    // its own. The stairwell sits inside this footprint, so the clamp holds
    // all the way down.
    pos.x = clamp(pos.x, SUBFAB.minX + PLAYER_RADIUS, SUBFAB.maxX - PLAYER_RADIUS)
    pos.z = clamp(pos.z, SUBFAB.minZ + PLAYER_RADIUS, SUBFAB.maxZ - PLAYER_RADIUS)
  } else {
    const width = FAB.bayDepth * 2 + FAB.aisleHalfWidth * 2
    const halfL = FAB.length / 2 - PLAYER_RADIUS - 0.15
    const halfW = width / 2 - PLAYER_RADIUS - 0.15
    pos.x = clamp(pos.x, -halfL, halfL)
    pos.z = clamp(pos.z, -halfW, halfW)
  }

  // The player as a capsule from ankles to head, so colliders confined to
  // one level never touch the student walking the other.
  const low = foot + 0.2
  const high = foot + 1.8

  for (const c of colliders) {
    if ((c.maxY !== undefined && c.maxY < low) || (c.minY !== undefined && c.minY > high))
      continue
    const minX = c.minX - PLAYER_RADIUS
    const maxX = c.maxX + PLAYER_RADIUS
    const minZ = c.minZ - PLAYER_RADIUS
    const maxZ = c.maxZ + PLAYER_RADIUS
    if (pos.x <= minX || pos.x >= maxX || pos.z <= minZ || pos.z >= maxZ) continue

    const pushLeft = pos.x - minX
    const pushRight = maxX - pos.x
    const pushBack = pos.z - minZ
    const pushFront = maxZ - pos.z
    const smallest = Math.min(pushLeft, pushRight, pushBack, pushFront)

    // Kill only the velocity component driving into the wall, and only when
    // it points inward. The first version damped the WHOLE vector on every
    // contact frame, which at 60 fps also erased the tangential part - so
    // brushing a tool while walking diagonally ground the student to a halt
    // against it instead of sliding along its face.
    if (smallest === pushLeft) {
      pos.x = minX
      if (velocity) velocity.x = Math.min(velocity.x, 0)
    } else if (smallest === pushRight) {
      pos.x = maxX
      if (velocity) velocity.x = Math.max(velocity.x, 0)
    } else if (smallest === pushBack) {
      pos.z = minZ
      if (velocity) velocity.z = Math.min(velocity.z, 0)
    } else {
      pos.z = maxZ
      if (velocity) velocity.z = Math.max(velocity.z, 0)
    }
  }
}
